import React, { useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { FaTag } from 'react-icons/fa6';
import { HiOutlineShieldCheck } from 'react-icons/hi';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { toast } from 'react-toastify';
import styles from './style.module.scss';
import { formatCurrency } from '../../hooks/useCheckoutTotals';

/**
 * Casca comum das três formas de pagamento.
 *
 * Antes cada componente repetia resumo do pedido, cupom, termos e botão de
 * confirmar — três cópias do mesmo markup. Agora o método de pagamento só
 * entrega o próprio conteúdo (children) e o resto vem daqui:
 *
 *  - resumo do pedido fixo na lateral no desktop;
 *  - barra de pagamento fixa no rodapé no mobile, com total e botão sempre à mão;
 *  - cupom recolhido por padrão, para não empurrar o botão de pagar para baixo;
 *  - aceite dos termos logo acima do botão, com erro inline em vez de só um toast.
 */
const CheckoutBase = ({
  resumo,
  couponCode,
  setCouponCode,
  validarCupon,
  mostrarCupom = true,
  metodoPix = false,
  confirmLabel = 'Confirmar compra',
  loading = false,
  onConfirm,
  children,
}) => {
  const [cupomAberto, setCupomAberto] = useState(false);
  const [cupomAplicado, setCupomAplicado] = useState(false);
  const [aplicandoCupom, setAplicandoCupom] = useState(false);
  const [aceiteTermos, setAceiteTermos] = useState(false);
  const [erroTermos, setErroTermos] = useState(false);
  const termosRef = useRef(null);

  const aplicarCupom = async () => {
    if (!couponCode?.trim()) {
      toast.warn('Digite um cupom para aplicar.');
      return;
    }

    setAplicandoCupom(true);
    try {
      const valido = await validarCupon(metodoPix);
      if (valido) {
        setCupomAplicado(true);
        toast.success('Cupom aplicado com sucesso!');
        return;
      }
      toast.error('Cupom inválido.');
    } finally {
      setAplicandoCupom(false);
    }
  };

  const confirmar = () => {
    if (!aceiteTermos) {
      setErroTermos(true);
      // No mobile o botão fica na barra fixa e o aceite no resumo:
      // leva o usuário até o checkbox em vez de só mostrar um toast.
      termosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErroTermos(false);
    onConfirm();
  };

  const aceitarTermos = (checked) => {
    setAceiteTermos(checked);
    if (checked) setErroTermos(false);
  };

  const resumoPedido = (
    <div className={styles.summaryCard}>
      <h2 className={styles.summaryTitle}>Resumo do pedido</h2>

      <div className={styles.summaryItems}>
        {Object.keys(resumo.totals).map((nome) => (
          <div key={nome} className={styles.summaryRow}>
            <span>{nome}</span>
            <strong>{formatCurrency(resumo.totals[nome])}</strong>
          </div>
        ))}

        {resumo.temDesconto && (
          <div className={`${styles.summaryRow} ${styles.discountRow}`}>
            <span>Cupom {couponCode ? `(${couponCode})` : ''}</span>
            <strong>-{formatCurrency(resumo.desconto)}</strong>
          </div>
        )}
      </div>

      <div className={styles.totalRow}>
        <span>Total</span>
        <strong>{formatCurrency(resumo.total)}</strong>
      </div>

      {mostrarCupom && (
        <div className={styles.couponBlock}>
          {!cupomAberto && !cupomAplicado ? (
            <button type='button' className={styles.couponToggle} onClick={() => setCupomAberto(true)}>
              <FaTag />
              <span>Tenho um cupom de desconto</span>
              <MdKeyboardArrowDown />
            </button>
          ) : (
            <div className={styles.couponForm}>
              <Input
                placeholder='Digite seu cupom'
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                disabled={cupomAplicado}
                onPressEnter={aplicarCupom}
                className={styles.couponInput}
              />
              <Button
                className={styles.couponButton}
                onClick={aplicarCupom}
                disabled={cupomAplicado}
                loading={aplicandoCupom}
              >
                {cupomAplicado ? 'Aplicado' : 'Aplicar'}
              </Button>
            </div>
          )}
        </div>
      )}

      <label ref={termosRef} className={`${styles.terms} ${erroTermos ? styles.termsError : ''}`}>
        <input
          type='checkbox'
          checked={aceiteTermos}
          onChange={(e) => aceitarTermos(e.target.checked)}
        />
        <span>
          Li e concordo com os <strong>Termos de Uso</strong> e a <strong>Política de Privacidade</strong>
        </span>
      </label>

      {erroTermos && (
        <p className={styles.termsErrorText} role='alert'>
          Marque o aceite acima para continuar.
        </p>
      )}

      <Button
        type='primary'
        block
        loading={loading}
        disabled={loading}
        onClick={confirmar}
        className={styles.confirmButton}
      >
        {confirmLabel}
      </Button>

      <div className={styles.secureNote}>
        <HiOutlineShieldCheck />
        <span>Ambiente protegido. A confirmação é automática após o pagamento.</span>
      </div>
    </div>
  );

  return (
    <div className={styles.checkout}>
      <div className={styles.grid}>
        <section className={styles.content}>{children}</section>
        <aside className={styles.sidebar}>{resumoPedido}</aside>
      </div>

      {/* Espaçador para o conteúdo não ficar escondido atrás da barra fixa */}
      <div className={styles.mobileBarSpacer} aria-hidden='true' />

      <div className={styles.mobileBar}>
        <div className={styles.mobileBarTotal}>
          <span>Total</span>
          <strong>{formatCurrency(resumo.total)}</strong>
        </div>
        <Button
          type='primary'
          loading={loading}
          disabled={loading}
          onClick={confirmar}
          className={styles.mobileBarButton}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
};

export default CheckoutBase;
